import { useState, useEffect, useRef } from 'react';

interface UseTeleprompterLinesProps {
  content: string;
  fontSize: number;
  containerWidth: number;
  paddingLeft?: number; // pixel value
  paddingRight?: number; // pixel value
  measureContainerRef?: React.RefObject<HTMLDivElement>; // Optional custom container for measurement
}

export function useTeleprompterLines({
  content,
  fontSize,
  containerWidth,
  paddingLeft = 0,
  paddingRight = 0,
}: UseTeleprompterLinesProps) {
  const [lines, setLines] = useState<string[]>([]);

  useEffect(() => {
    if (!content || containerWidth <= 0) {
      setLines(content ? content.split('\n') : []);
      return;
    }

    const calculateLines = () => {
      // Create a hidden canvas for measuring text
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      // Match the app's font stack: "Poppins", system-ui, -apple-system, sans-serif
      context.font = `${fontSize}px "Poppins", system-ui, -apple-system, sans-serif`; 
      
      const maxWidth = containerWidth - paddingLeft - paddingRight;
      const paragraphs = content.split('\n');
      const newLines: string[] = [];

      paragraphs.forEach(paragraph => {
        if (paragraph === '') {
          newLines.push('');
          return;
        }

        // Improved logic for CJK support:
        // We cannot simply split by space. We need to measure effectively.
        // Simple approach: Build line character by character (or word by word for English).
        
        let currentLine = '';
        let currentLineWidth = 0;
        
        // Tokenize: Split by spaces but keep them to preserve English word boundaries. 
        // For CJK, we effectively treat them as individual chars in the loop below if we iterate string directly.
        // A robust way without complex tokenization:
        // Iterate chars. If adding char exceeds width -> push line.
        // Exception: Don't break English words in the middle if possible.
        
        const chars = Array.from(paragraph); // Handle surrogate pairs
        let wordBuffer = '';
        let wordBufferWidth = 0;

        for (let i = 0; i < chars.length; i++) {
            const char = chars[i];
            const charWidth = context.measureText(char).width;
            
            // Heuristic: Is this a CJK character or a space?
            // Space or CJK char usually means "safe to break after".
            // English letter -> accumulate in wordBuffer.
            // Actually, simplest consistent behavior for mixed text:
            // 1. Accumulate chars into wordBuffer until we hit a "break point" (space or CJK char).
            // 2. Or simplified: Just measure char by char. If it fits, add. If not, break.
            //    BUT, for English, we shouldn't break mid-word.
            
            // Check if char is CJK (roughly) or space
            const isCJK = /[\u4e00-\u9fa5\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uffef]/.test(char);
            const isSpace = /\s/.test(char);
            
            if (isCJK || isSpace) {
                // Formatting the buffer we had so far (e.g. previous English word)
                if (wordBuffer) {
                    if (currentLineWidth + wordBufferWidth > maxWidth && currentLineWidth > 0) {
                        newLines.push(currentLine);
                        currentLine = '';
                        currentLineWidth = 0;
                    }
                    currentLine += wordBuffer;
                    currentLineWidth += wordBufferWidth;
                    wordBuffer = '';
                    wordBufferWidth = 0;
                }

                // Now handle the CJK char or Space itself
                if (currentLineWidth + charWidth > maxWidth && currentLineWidth > 0) {
                    newLines.push(currentLine);
                    currentLine = '';
                    currentLineWidth = 0;
                    // If it was space, we might skip it at start of new line?
                    // Usually yes.
                    if (isSpace) continue; 
                }
                
                currentLine += char;
                currentLineWidth += charWidth;
            } else {
                // It's likely an English/Latin letter/number/symbol
                // Accumulate to buffer
                wordBuffer += char;
                wordBufferWidth += charWidth;
                
                // Safety valve: if a single word is wider than the WHOLE container, force break it
                if (wordBufferWidth > maxWidth) {
                     newLines.push(currentLine + wordBuffer.slice(0, -1));
                     currentLine = '';
                     currentLineWidth = 0;
                     // Keep the last char in buffer
                     const lastChar = wordBuffer.slice(-1);
                     const lastCharWidth = context.measureText(lastChar).width;
                     wordBuffer = lastChar;
                     wordBufferWidth = lastCharWidth;
                }
            }
        }
        
        // flush remaining buffer
        if (wordBuffer) {
             if (currentLineWidth + wordBufferWidth > maxWidth && currentLineWidth > 0) {
                newLines.push(currentLine);
                currentLine = wordBuffer;
            } else {
                currentLine += wordBuffer;
            }
        }
        
        if (currentLine) {
            newLines.push(currentLine);
        }
      });

      setLines(newLines);
    };

    calculateLines();
  }, [content, fontSize, containerWidth, paddingLeft, paddingRight]);

  return lines;
}
